import React, { useState, useEffect } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { getAdminJobDetails, getAdminJobLogs } from '../../api'; // Adjust path
import Breadcrumbs from '../../components/Breadcrumbs'; // Import Breadcrumbs

// Re-use helpers from AdminJobsPage (or move to a shared utils file)
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0 || !bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const getStatusClass = (status) => {
    switch (status) {
        case 'SUCCESS':
            return 'bg-success-soft text-success-soft-foreground border border-success-border';
        case 'FAILED':
            return 'bg-danger-soft text-danger-soft-foreground border border-danger-border';
        case 'PROCESSING':
            return 'bg-info-soft text-info-soft-foreground border border-info-border';
        case 'PENDING':
            return 'bg-warning-soft text-warning-soft-foreground border border-warning-border';
        default:
            return 'bg-background-soft text-muted-foreground border border-border';
    }
};

const getLogLevelClass = (level) => {
    switch (level) {
        case 'ERROR':
        case 'CRITICAL': return 'text-red-400';
        case 'WARNING': return 'text-yellow-400';
        case 'INFO': return 'text-blue-400';
        case 'DEBUG': return 'text-gray-500';
        default: return 'text-gray-400';
    }
};

const AdminJobDetailsPage = () => {
    const { jobId } = useParams({});
    // Get potential user filter info from search params
    const { user_id: userIdFilter, username: usernameFilter } = useSearch({});
    
    const [job, setJob] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            setError(null);
            // Reset job state on new fetch to avoid showing old data briefly
            setJob(null); 
            setLogs([]); 
            try {
                const [jobDetails, jobLogs] = await Promise.all([
                    getAdminJobDetails(jobId),
                    getAdminJobLogs(jobId)
                ]);
                setJob(jobDetails);
                setLogs(Array.isArray(jobLogs) ? jobLogs : []);
            } catch (err) {
                console.error("Failed to fetch job details or logs:", err);
                setError(err.message || 'Failed to load job data.');
            } finally {
                setLoading(false);
            }
        };

        if (jobId) {
            fetchDetails();
        }
    }, [jobId]);

    // Define breadcrumbs before loading checks
    let crumbs;
    const baseCrumbs = [
        { label: 'Admin', to: '/app/admin' },
    ];
    const jobCrumbLabel = jobId ? `Job ${jobId.substring(0, 8)}...` : 'Job details';

    if (userIdFilter) {
        crumbs = [
            ...baseCrumbs,
            { label: 'Users', to: '/app/admin/users' },
            {
                label: `Jobs for ${usernameFilter || userIdFilter || '...'}`,
                to: '/app/admin/jobs',
                search: { user_id: userIdFilter, username: usernameFilter }
            },
            { label: jobCrumbLabel } // Current page
        ];
    } else {
        crumbs = [
            ...baseCrumbs,
            { label: 'All Jobs', to: '/app/admin/jobs' },
            { label: jobCrumbLabel } // Current page
        ];
    }

    return (
        <div className="text-foreground">
            <Breadcrumbs crumbs={crumbs} />

            {/* Conditional rendering for page content based on loading/error state */}
            {loading && <div className="text-foreground">Loading job details...</div>}
            {error && <div className="text-danger">Error: {error}</div>}
            {!loading && !error && !job && <div className="text-foreground">Job not found.</div>}

            {/* Render content only when loaded and available */}
            {!loading && !error && job && (
                <>
                    <h1 className="text-2xl font-semibold mb-6">Job details: <code>{jobId}</code></h1>
                    
                    {/* Job Summary Card */}
                    <div className="bg-background-elevated border border-border shadow-md rounded-lg p-6 mb-6">
                        <h2 className="text-xl font-semibold mb-4">Summary</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div><strong>Filename:</strong> {job.file_name}</div>
                            <div><strong>User ID:</strong> <code>{job.user_id}</code></div>
                            <div><strong>Status:</strong> <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusClass(job.status)}`}>{job.status}</span></div>
                            <div><strong>Created at:</strong> {new Date(job.created_at).toLocaleString()}</div>
                            <div><strong>Original size:</strong> {formatBytes(job.original_file_size)}</div>
                            <div><strong>Compressed size:</strong> {formatBytes(job.compressed_file_size)}</div>
                            <div><strong>Compression ratio:</strong> {job.compression_ratio ? `${(job.compression_ratio * 100).toFixed(1)}%` : 'N/A'}</div>
                             <div><strong>Original URL:</strong> {job.original_file_url ? <a href={job.original_file_url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline hover:text-accent/90">Link</a> : 'N/A'}</div>
                             <div><strong>Compressed URL:</strong> {job.compressed_file_url ? <a href={job.compressed_file_url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline hover:text-accent/90">Link</a> : 'N/A'}</div>
                        </div>
                    </div>

                    {/* Job Logs Card */}
                    <div className="bg-background-elevated border border-border shadow-md rounded-lg p-6">
                         <h2 className="text-xl font-semibold mb-4">Logs</h2>
                         {logs.length > 0 ? (
                            <div className="font-mono text-xs bg-background-soft border border-border p-4 rounded overflow-x-auto max-h-96">
                                {logs.map((log) => (
                                    <div key={log.id} className="whitespace-pre-wrap mb-1">
                                        <span className="text-muted-foreground mr-2">{new Date(log.timestamp).toLocaleString()}</span>
                                        <span className={`font-semibold mr-2 ${getLogLevelClass(log.level)}`}>[{log.level}]</span>
                                        <span className="text-foreground/90">{log.message}</span>
                                    </div>
                                ))}
                            </div>
                         ) : (
                             <p className="text-muted-foreground">No logs found for this job.</p>
                         )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminJobDetailsPage; 